// Collab AI API Service Layer
// Connects React frontend to FastAPI backend

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

// Helper function for API calls
const apiCall = async (endpoint, options = {}) => {
  const token = localStorage.getItem('access_token');
  
  const headers = {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` }),
    ...options.headers
  };

  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers
    });

    if (response.status === 401) {
      // Token expired, try to refresh
      const refreshed = await refreshToken();
      if (refreshed) {
        // Retry the original request with new token
        return apiCall(endpoint, options);
      } else {
        // Refresh failed, redirect to login
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        window.location.href = '/login';
        throw new Error('Authentication failed');
      }
    }

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'API request failed');
    }

    return await response.json();
  } catch (error) {
    console.error('API Error:', error);
    throw error;
  }
};

// Auth helpers
const refreshToken = async () => {
  const refresh = localStorage.getItem('refresh_token');
  if (!refresh) return false;

  try {
    const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refresh })
    });

    if (response.ok) {
      const data = await response.json();
      localStorage.setItem('access_token', data.access_token);
      return true;
    }
    return false;
  } catch {
    return false;
  }
};

// API Service Object
const api = {
  // ============================================
  // AUTH ENDPOINTS
  // ============================================
  auth: {
    login: async (email, password) => {
      const response = await apiCall('/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password })
      });
      
      localStorage.setItem('access_token', response.access_token);
      localStorage.setItem('refresh_token', response.refresh_token);
      
      return response;
    },

    register: async (email, password, organizationName) => {
      return apiCall('/auth/register', {
        method: 'POST',
        body: JSON.stringify({ email, password, organization_name: organizationName })
      });
    },

    logout: () => {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      window.location.href = '/login';
    },

    getCurrentUser: async () => {
      return apiCall('/auth/me');
    }
  },

  // ============================================
  // LEADS ENDPOINTS
  // ============================================
  leads: {
    list: async (params = {}) => {
      const queryString = new URLSearchParams(params).toString();
      return apiCall(`/leads${queryString ? `?${queryString}` : ''}`);
    },

    get: async (leadId) => {
      return apiCall(`/leads/${leadId}`);
    },

    create: async (leadData) => {
      return apiCall('/leads', {
        method: 'POST',
        body: JSON.stringify(leadData)
      });
    },

    update: async (leadId, updates) => {
      return apiCall(`/leads/${leadId}`, {
        method: 'PUT',
        body: JSON.stringify(updates)
      });
    },

    delete: async (leadId) => {
      return apiCall(`/leads/${leadId}`, {
        method: 'DELETE'
      });
    },

    close: async (leadId, isWin, closeData = {}) => {
      return apiCall(`/leads/${leadId}/close`, {
        method: 'POST',
        body: JSON.stringify({
          is_win: isWin,
          ...closeData
        })
      });
    },

    // Simulate a new lead (for testing)
    simulate: async (leadType = 'web_form') => {
      return apiCall('/leads/simulate', {
        method: 'POST',
        body: JSON.stringify({ lead_type: leadType })
      });
    }
  },

  // ============================================
  // TEAM ENDPOINTS
  // ============================================
  team: {
    list: async () => {
      return apiCall('/team');
    },

    get: async (memberId) => {
      return apiCall(`/team/${memberId}`);
    },

    create: async (memberData) => {
      return apiCall('/team', {
        method: 'POST',
        body: JSON.stringify(memberData)
      });
    },

    update: async (memberId, updates) => {
      return apiCall(`/team/${memberId}`, {
        method: 'PUT',
        body: JSON.stringify(updates)
      });
    },

    delete: async (memberId) => {
      return apiCall(`/team/${memberId}`, {
        method: 'DELETE'
      });
    },

    updateStatus: async (memberId, status, metadata = {}) => {
      return apiCall(`/team/${memberId}/status`, {
        method: 'PUT',
        body: JSON.stringify({
          status,
          ...metadata
        })
      });
    }
  },

  // ============================================
  // ROUTING RULES ENDPOINTS
  // ============================================
  routingRules: {
    list: async () => {
      return apiCall('/routing-rules');
    },

    create: async (ruleData) => {
      return apiCall('/routing-rules', {
        method: 'POST',
        body: JSON.stringify(ruleData)
      });
    },

    update: async (ruleId, updates) => {
      return apiCall(`/routing-rules/${ruleId}`, {
        method: 'PUT',
        body: JSON.stringify(updates)
      });
    },

    delete: async (ruleId) => {
      return apiCall(`/routing-rules/${ruleId}`, {
        method: 'DELETE'
      });
    },

    // Parse natural language rule
    parseNaturalLanguage: async (text) => {
      return apiCall('/routing-rules/parse', {
        method: 'POST',
        body: JSON.stringify({ text })
      });
    },

    // Test a rule against sample data
    test: async (ruleId, testData) => {
      return apiCall(`/routing-rules/${ruleId}/test`, {
        method: 'POST',
        body: JSON.stringify(testData)
      });
    }
  },

  // ============================================
  // ACTIONS ENDPOINTS (for Action Required Panel)
  // ============================================
  actions: {
    listPending: async () => {
      return apiCall('/actions/pending');
    },

    get: async (actionId) => {
      return apiCall(`/actions/${actionId}`);
    },

    resolve: async (actionId, resolution) => {
      return apiCall(`/actions/${actionId}/resolve`, {
        method: 'POST',
        body: JSON.stringify({ resolution })
      });
    },

    // Submit manager instruction for AI to process
    submitInstruction: async (actionId, instruction) => {
      return apiCall(`/actions/${actionId}/instruct`, {
        method: 'POST',
        body: JSON.stringify({ instruction })
      });
    }
  },

  // ============================================
  // METRICS & ANALYTICS ENDPOINTS
  // ============================================
  metrics: {
    getDashboard: async (timeRange = '7d') => {
      return apiCall(`/metrics/dashboard?range=${timeRange}`);
    },

    getTeamPerformance: async (timeRange = '30d') => {
      return apiCall(`/metrics/team?range=${timeRange}`);
    },

    getAIConfidenceTrend: async (weeks = 24) => {
      return apiCall(`/metrics/ai-confidence?weeks=${weeks}`);
    },

    getOverridesTrend: async (months = 6) => {
      return apiCall(`/metrics/overrides?months=${months}`);
    },

    getLeadSourceBreakdown: async () => {
      return apiCall('/metrics/lead-sources');
    },

    getResponseTimes: async () => {
      return apiCall('/metrics/response-times');
    }
  },

  // ============================================
  // REPORTS ENDPOINTS
  // ============================================
  reports: {
    generate: async (reportType, params = {}) => {
      return apiCall('/reports/generate', {
        method: 'POST',
        body: JSON.stringify({
          report_type: reportType,
          ...params
        })
      });
    },

    download: async (reportId) => {
      const token = localStorage.getItem('access_token');
      window.open(`${API_BASE_URL}/reports/${reportId}/download?token=${token}`, '_blank');
    },

    list: async () => {
      return apiCall('/reports');
    }
  },

  // ============================================
  // SETTINGS ENDPOINTS
  // ============================================
  settings: {
    get: async () => {
      return apiCall('/settings');
    },

    update: async (updates) => {
      return apiCall('/settings', {
        method: 'PUT',
        body: JSON.stringify(updates)
      });
    },

    updateConfidenceThreshold: async (threshold) => {
      return apiCall('/settings/confidence-threshold', {
        method: 'PUT',
        body: JSON.stringify({ threshold })
      });
    },

    // Forms repository
    forms: {
      list: async () => {
        return apiCall('/settings/forms');
      },

      upload: async (formFile, metadata) => {
        const formData = new FormData();
        formData.append('file', formFile);
        formData.append('metadata', JSON.stringify(metadata));

        const token = localStorage.getItem('access_token');
        
        const response = await fetch(`${API_BASE_URL}/settings/forms`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });

        if (!response.ok) throw new Error('Form upload failed');
        return response.json();
      },

      delete: async (formId) => {
        return apiCall(`/settings/forms/${formId}`, {
          method: 'DELETE'
        });
      }
    }
  },

  // ============================================
  // OVERRIDES ENDPOINT
  // ============================================
  overrides: {
    create: async (leadId, originalAssignment, manualAssignment, reason) => {
      return apiCall('/overrides', {
        method: 'POST',
        body: JSON.stringify({
          lead_id: leadId,
          original_assignment: originalAssignment,
          manual_assignment: manualAssignment,
          reason
        })
      });
    },

    list: async (params = {}) => {
      const queryString = new URLSearchParams(params).toString();
      return apiCall(`/overrides${queryString ? `?${queryString}` : ''}`);
    }
  }
};

export default api;